<template>
  <div class="flex">
    <div class="w-60">
      <el-select
        v-model="_searchItem.key"
        :key="_searchItem.key"
        size="medium"
        :placeholder="$t('selectOne')"
        class="w-full"
        @change="onKeyChanged"
      >
        <el-option v-for="(value, key) in customKeySelectDatas" :key="key" :label="$t('barcode.' + key)" :value="value">
          {{ $t('barcode.' + key) }}
        </el-option>
      </el-select>
    </div>
    <div class="flex-grow">
      <div class="flex">
        <SearchItemBarcodeType
          v-if="_searchItem.key === customKeySelectDatas.barcodeType"
          v-model:searchItem="_searchItem"
          @change:barcodeType="changeBarcodeType"
          :bcTypes="bcTypes"
        />
        <SearchItemCustomField
          v-else-if="_searchItem.key === customKeySelectDatas.customField"
          v-model:searchItem="_searchItem"
          :projectCustomFields="projectCustomFields"
        />
        <SearchItemTrackingData
          v-else-if="_searchItem.key === customKeySelectDatas.trackingData"
          v-model:searchItem="_searchItem"
        />
        <component
          v-else
          :is="getSearchItemComponent(_searchItem.key).component"
          v-bind="getSearchItemComponent(_searchItem.key).props"
          v-model:searchItem="_searchItem"
        />

        <div class="flex items-center w-32 pl-3 btn-controller">
          <el-button
            type="primary"
            v-if="isAddButton"
            icon="el-icon-plus"
            size="small"
            circle
            @click="$emit('add:searchItem')"
          />

          <el-dropdown trigger="hover" placement="bottom-end">
            <el-button class="remove-btn three-dots-btn" size="small" text>
              <IconVertialThreeDotCircle color="#525252" class="w-7 h-7" />
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="$emit('clear:searchItem', index)">
                  <div class="flex items-center">
                    <IconCancel class="mr-2" color="#525252" />
                    <span>{{ $t('clearAll') }}</span>
                  </div>
                </el-dropdown-item>
                <el-dropdown-item v-if="isRemoveButton" divided @click="$emit('remove:searchItem', index)">
                  <div class="flex items-center hover:text-red-500">
                    <IconTrash class="mr-2" />
                    <span>{{ $t('remove') }}</span>
                  </div>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import SearchItemAddress from '@/components/barcodeSearch/SearchItemAddress.vue'
import SearchItemBarcodeType from '@/components/barcodeSearch/SearchItemBarcodeType.vue'
import SearchItemBoolean from '@/components/barcodeSearch/SearchItemBoolean.vue'
import SearchItemCustomField from '@/components/barcodeSearch/SearchItemCustomField.vue'
import SearchItemDate from '@/components/barcodeSearch/SearchItemDate.vue'
import SearchItemDateTime from '@/components/barcodeSearch/SearchItemDateTime.vue'
import SearchItemDimension from '@/components/barcodeSearch/SearchItemDimension.vue'
import SearchItemTextbox from '@/components/barcodeSearch/SearchItemTextbox.vue'
import SearchItemTrackingData from '@/components/barcodeSearch/SearchItemTrackingData.vue'
import SearchItemNumber from '@/components/barcodeSearch/SearchItemNumber.vue'
import SearchItemTrackpoint from '@/components/barcodeSearch/SearchItemTrackpoint.vue'
import SearchItemUser from '@/components/barcodeSearch/SearchItemUser.vue'
import IconTrash from '@/components/svg/IconTrash.vue'
import IconVertialThreeDotCircle from '@/components/svg/IconVertialThreeDotCircle.vue'
import IconCancel from '@/components/svg/IconCancel.vue'
import { BARCODE_SEARCH_KEYS, BARCODE_SEARCH_VALUE_TYPES } from '@/utils/constants'
import { Options, Vue } from 'vue-class-component'
import { InjectReactive, Prop, PropSync } from 'vue-property-decorator'
import { IBarcodeSearchFormItem, ITrackpoint, TArrProjectCustomField } from '@/utils/types'
import SearchItemMarumiya from './SearchItemMarumiya.vue'
import SearchItemSupplier from './SearchItemSupplier.vue'

@Options({
  components: {
    SearchItemDate,
    SearchItemTextbox,
    SearchItemUser,
    SearchItemTrackpoint,
    SearchItemAddress,
    SearchItemDimension,
    SearchItemCustomField,
    SearchItemBoolean,
    SearchItemBarcodeType,
    SearchItemTrackingData,
    IconTrash,
    IconVertialThreeDotCircle,
    IconCancel,
  },
  emits: ['update:searchItem', 'remove:searchItem', 'add:searchItem', 'clear:searchItem', 'change:barcodeType'],
  name: 'SearchItem',
})
export default class SearchItem extends Vue {
  @InjectReactive() projectVersion!: number
  @PropSync('searchItem', { type: Object }) _searchItem!: IBarcodeSearchFormItem
  @Prop({ type: Number }) readonly index!: number
  @Prop({ type: Boolean }) readonly isRemoveButton!: false
  @Prop({ type: Boolean }) readonly isAddButton!: false
  @Prop({ type: Array }) readonly projectCustomFields!: TArrProjectCustomField[]
  @Prop({ type: Object }) readonly barcodeTypes?: {}
  @Prop({ type: Object }) readonly trackPoints?: {}

  get customKeySelectDatas() {
    const result = { ...BARCODE_SEARCH_KEYS }
    if (this.projectVersion === 0) {
      delete result.currentTrackpoint
      delete result.barcodeType
    }
    if (!this.isEnableSearchTrackingData) {
      delete result.trackingData
    }

    return result
  }

  get isEnableSearchTrackingData() {
    return this.$store.getters.isEnableSearchTrackingData
  }

  get trackpoints(): Record<string, ITrackpoint> {
    return (
      this.$store.state.barcode?.projectList?.trackPoints ??
      this.trackPoints ??
      this.$store.state.barcode?.projectDetail?.trackPoints ??
      {}
    )
  }

  get bcTypes() {
    return (
      this.$store.state.barcode.projectList.barcodeTypes ||
      this.$store.state.barcode.projectDetail.barcodeTypes ||
      this.barcodeTypes ||
      {}
    )
  }

  onKeyChanged() {
    const searchItem = {
      key: this._searchItem.key,
      valueType: '',
      bcTypeKey: '',
      subKey: '',
      componentType: '',
      maxValue: '',
      minValue: '',
      value: '',
      valueList: [],
      isExactValue: false,
    } as {
      key: string
      valueType: string
      bcTypeKey: string
      subKey: string
      componentType: string
      maxValue: string
      minValue: string
      value: string | number
      valueList: string[]
      isExactValue: boolean
    }
    searchItem.isExactValue = true

    switch (searchItem.key) {
      case BARCODE_SEARCH_KEYS.createdDate:
      case BARCODE_SEARCH_KEYS.updatedDate:
      case BARCODE_SEARCH_KEYS.lastTrackingDateTime:
      case BARCODE_SEARCH_KEYS.activatedDateTime:
      case BARCODE_SEARCH_KEYS.manufactureCompletionDate:
        searchItem.valueType = BARCODE_SEARCH_VALUE_TYPES.date
        break
      case BARCODE_SEARCH_KEYS.createdAt:
        searchItem.valueType = BARCODE_SEARCH_VALUE_TYPES.dateTimeString
        break
      case BARCODE_SEARCH_KEYS.createdUser:
      case BARCODE_SEARCH_KEYS.updatedUser:
        searchItem.valueType = BARCODE_SEARCH_VALUE_TYPES.string
        break
      case BARCODE_SEARCH_KEYS.origin:
      case BARCODE_SEARCH_KEYS.destination:
        searchItem.valueType = BARCODE_SEARCH_VALUE_TYPES.string
        break
      case BARCODE_SEARCH_KEYS.dimension:
        searchItem.valueType = BARCODE_SEARCH_VALUE_TYPES.double
        break
      case BARCODE_SEARCH_KEYS.currentTrackpoint:
      case BARCODE_SEARCH_KEYS.currentTrackPointKey:
        searchItem.valueType = BARCODE_SEARCH_VALUE_TYPES.string
        break
      case BARCODE_SEARCH_KEYS.isActivated:
      case BARCODE_SEARCH_KEYS.isDeactivated:
      case BARCODE_SEARCH_KEYS.isActive:
        searchItem.valueType = BARCODE_SEARCH_VALUE_TYPES.boolean
        break
      case BARCODE_SEARCH_KEYS.barcodeType:
        searchItem.valueType = BARCODE_SEARCH_VALUE_TYPES.string
        break
      case BARCODE_SEARCH_KEYS.customField:
        searchItem.valueType = searchItem.valueType !== '' ? searchItem.valueType : BARCODE_SEARCH_VALUE_TYPES.string
        break
      case BARCODE_SEARCH_KEYS.orderNumber:
      case BARCODE_SEARCH_KEYS.manufacturedItemCode:
      case BARCODE_SEARCH_KEYS.componentItemCode:
        searchItem.isExactValue = false
        searchItem.valueType = BARCODE_SEARCH_VALUE_TYPES.string
        break
      case BARCODE_SEARCH_KEYS.recycleCount:
        searchItem.value = 0
        searchItem.valueType = BARCODE_SEARCH_VALUE_TYPES.integer
        break
      case BARCODE_SEARCH_KEYS.warehouseId:
      case BARCODE_SEARCH_KEYS.workCenterId:
      case BARCODE_SEARCH_KEYS.supplierId:
        searchItem.value = ''
        searchItem.valueType = BARCODE_SEARCH_VALUE_TYPES.integer
        break
      case BARCODE_SEARCH_KEYS.trackingNumber:
      default:
        searchItem.valueType = BARCODE_SEARCH_VALUE_TYPES.string
        break
    }
    this._searchItem = searchItem as IBarcodeSearchFormItem
  }

  getSearchItemComponent(searchKey: string) {
    switch (searchKey) {
      case BARCODE_SEARCH_KEYS.createdDate:
      case BARCODE_SEARCH_KEYS.updatedDate:
      case BARCODE_SEARCH_KEYS.activatedDateTime:
      case BARCODE_SEARCH_KEYS.lastTrackingDateTime:
      case BARCODE_SEARCH_KEYS.manufactureCompletionDate:
        return { component: SearchItemDate }
      case BARCODE_SEARCH_KEYS.createdAt:
        return { component: SearchItemDateTime }

      case BARCODE_SEARCH_KEYS.createdUser:
      case BARCODE_SEARCH_KEYS.updatedUser:
        return { component: SearchItemUser }

      case BARCODE_SEARCH_KEYS.origin:
      case BARCODE_SEARCH_KEYS.destination:
        return { component: SearchItemAddress }

      case BARCODE_SEARCH_KEYS.dimension:
        return { component: SearchItemDimension }

      case BARCODE_SEARCH_KEYS.currentTrackpoint:
        return { component: SearchItemTrackpoint, props: { trackPoints: this.trackpoints } }

      case BARCODE_SEARCH_KEYS.currentTrackPointKey:
        return { component: SearchItemMarumiya, props: { masterData: this.masterDataTrackingPoints } }

      case BARCODE_SEARCH_KEYS.warehouseId:
        return { component: SearchItemMarumiya, props: { masterData: this.masterDataWarehouses } }

      case BARCODE_SEARCH_KEYS.supplierId:
        return {
          component: SearchItemSupplier,
          props: {
            suppliersData: this.masterDataSuppliers,
            workCentersData: this.masterDataWorkcenters,
            warehousesData: this.masterDataWarehouses,
          },
        }
      case BARCODE_SEARCH_KEYS.workCenterId:
        return { component: SearchItemMarumiya, props: { masterData: this.masterDataWorkcenters } }

      case BARCODE_SEARCH_KEYS.isActivated:
      case BARCODE_SEARCH_KEYS.isDeactivated:
      case BARCODE_SEARCH_KEYS.isActive:
        return { component: SearchItemBoolean }
      case BARCODE_SEARCH_KEYS.recycleCount:
        return { component: SearchItemNumber }

      case BARCODE_SEARCH_KEYS.orderNumber:
      case BARCODE_SEARCH_KEYS.manufacturedItemCode:
      case BARCODE_SEARCH_KEYS.componentItemCode:
      case BARCODE_SEARCH_KEYS.trackingNumber:
      default:
        return { component: SearchItemTextbox }
    }
  }

  get masterDataTrackingPoints() {
    return this.$store.getters.getMasterDataTrackingPoints
  }

  get masterDataWorkcenters() {
    return this.$store.getters.getMasterDataWorkcenters
  }

  get masterDataWarehouses() {
    return this.$store.getters.getMasterDataWarehouses
  }

  get masterDataSuppliers() {
    return this.$store.getters.getMasterDataSuppliers
  }

  changeBarcodeType() {
    this.$emit('change:barcodeType')
  }
}
</script>
<style lang="scss" scoped>
.btn-controller :deep() {
  .el-button--small {
    padding: 7px;
    min-height: 28px !important;
    margin: 6px 12px 6px 0px;

    &.is-disabled {
      &.remove-btn {
        background-color: transparent !important;
        border-color: transparent !important;
      }
    }
    &.remove-btn {
      background-color: transparent;
      border-color: transparent;
    }
    &.three-dots-btn {
      padding: 0;
    }
  }
}
</style>
